import React, { useContext, useState } from 'react';
import { CountryInclusionMode } from '~/graphqlTypes/globalTypes';
import './CountryList.scss';

import { CountryListIcon } from './CountryListIcon';
import { BEM } from '@feedfm/feed-util';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { OverlayChildren } from 'react-bootstrap/esm/Overlay';
import { CountryDateFragment } from '~/graphqlTypes/CountryDateFragment';
import { AppContext } from '~/AppContext';
import { normalizeCountries } from '~/util/normalizeCountries';

export interface CountryListProps {
  className?: string;
  /** The country inclusion mode. */
  mode: CountryInclusionMode;
  /** A list of country codes or various fragments.` */
  countries: null | Array<string | CountryDateFragment>;
  /**
   * The there are more than this number of countries, the rest are tucked into a popover. Default:
   * 5.
   */
  maxExplicitCountries?: number;
  /**
   * Display a short version of the list. This will not include the normalized list of codes and
   * will only show the first few countries. Default: false.
   */
  short?: boolean;
  /** Display inline instead of as a block. */
  inline?: boolean;
  /**
   * "Active" voice uses phrasing like "Include countries X" or "Exclude countries Y". "Passive
   * positive" voice uses lower case and phrasing like "A, B, C" or "all except X".
   */
  voice?: 'active' | 'passive';
}

const bem = new BEM( 'fmg-country-list' );

/**
 * Given a country availability, generate some nice text output describing it. This will be paired
 * with a deep UI listing all included countries, so this text is meant to be brief, informative,
 * and scannable.
 *
 * @param makePopover Callback for generating a popover of country codes.
 */
function textDescription(
  voice: 'active' | 'passive',
  mode: CountryInclusionMode,
  countryCodes: string[],
  maxExplicitCountries: number,
  makePopover: ( countryCodes: string[] ) => OverlayChildren,
): React.ReactNode {
  if ( mode === CountryInclusionMode.include_all ) {
    return voice === 'active' ? 'Include all countries' : 'all countries';
  }

  if ( mode === CountryInclusionMode.exclude_all ) {
    return voice === 'active' ? 'Exclude all countries' : 'no countries';
  }

  const givenCodes = countryCodes || [];

  if ( mode === CountryInclusionMode.include && givenCodes.length === 0 ) {
    return voice === 'active' ? 'Include no countries' : 'no countries';
  }

  if ( mode === CountryInclusionMode.exclude && givenCodes.length === 0 ) {
    return voice === 'active' ? 'Exclude no countries' : 'all countries';
  }

  if ( givenCodes.length <= maxExplicitCountries ) {
    return voice === 'active'
      ? `${mode === CountryInclusionMode.exclude ? 'Include all except ' : 'Include '}${givenCodes.join( ', ' )}`
      : `${mode === CountryInclusionMode.exclude ? 'all except ' : ''}${givenCodes.join( ', ' )}`;
  }

  const popover = makePopover( givenCodes );

  const prefix =
    voice === 'active'
      ? `${mode === CountryInclusionMode.exclude ? 'Include all except ' : 'Include '}`
      : `${mode === CountryInclusionMode.exclude ? 'all except ' : ''}`;

  return (
    <>
      { prefix }
      <OverlayTrigger trigger={ ['hover', 'focus'] } overlay={ popover }>
        <span tabIndex={ 0 } className={ BEM.$( bem.el( 'country-count' ) ) }>
          { givenCodes.length } countries
        </span>
      </OverlayTrigger>
    </>
  );
}

function toCountryCode( country: NonNullable<CountryListProps['countries']>[number] ) {
  return typeof country === 'string' ? country : country.country.code;
}

/**
 * Displays the list of countries associated with a country availability fragment. It includes a
 * text summary and a clickable icon for deeper diving into the list. The summary informs the user
 * of how the rule is defined (mode and given countries), and the icon provides a normalized include
 * list. The icon is only visible when all countries have been loaded from the backend.
 */
export const CountryList: React.FC<CountryListProps> = ( props ) => {
  const {
    mode,
    countries,
    className,
    inline = false,
    maxExplicitCountries = 5,
    short = false,
    voice = 'active',
  } = props;
  const { countries: allCountries } = useContext( AppContext );
  const countryCodes = countries?.map( toCountryCode ) || [];
  const normalizedCodes = normalizeCountries( mode, countryCodes, allCountries );

  const [popoverId] = useState( () => {
    const randomId = Math.random().toString( 36 ).substr( 2 );
    return `country-list-popover-${randomId}`;
  } );

  return (
    <div className={ BEM.$( className, bem.mod( { inline } ) ) }>
      { textDescription( voice, mode, countryCodes, maxExplicitCountries, ( extraCodes ) => (
        <Popover id={ popoverId }>
          <Popover.Title>
            <em>{ mode === CountryInclusionMode.include ? 'Inclusion list' : 'Exclusion list' }</em>
          </Popover.Title>
          <Popover.Content>{ extraCodes.join( ', ' ) }</Popover.Content>
        </Popover>
      ) ) }
      { short === false && normalizedCodes && normalizedCodes.length > 0 && (
        <CountryListIcon className={ BEM.$( bem, className ) } includedCountryCodes={ normalizedCodes } />
      ) }
    </div>
  );
};
